// Account form page (create + edit) — FE-040.

import { ArrowLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  Link, useNavigate, useParams,
} from "react-router-dom";

import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";

import { AccountForm } from "./AccountForm";
import {
  blankFormValues,
  serializeFormValues,
  validateAccountForm,
  type AccountFormErrors,
} from "./accountFormValidation";
import {
  useAccountDetailQuery,
  useCreateAccountMutation,
  useUpdateAccountMutation,
} from "./hooks";
import {
  AccountNotFoundError,
  type AccountDetail,
  type AccountFormMode,
  type AccountFormValues,
} from "./types";


export interface AccountFormPageProps {
  mode: AccountFormMode;
}


export function AccountFormPage({ mode }: AccountFormPageProps) {
  const navigate = useNavigate();
  const params = useParams();
  const accountId = mode === "edit" ? params.accountId : undefined;

  const detailQuery = useAccountDetailQuery(
    mode === "edit" ? accountId : undefined,
  );
  const createMutation = useCreateAccountMutation();
  const updateMutation = useUpdateAccountMutation();

  const initialValues = useMemo(
    () => deriveInitialValues(mode, detailQuery.data),
    [mode, detailQuery.data],
  );

  const [values, setValues] = useState<AccountFormValues>(initialValues);
  const [errors, setErrors] = useState<AccountFormErrors>({});

  // When the detail query lands the form is re-seeded once. After
  // that, user edits stay sticky (the dependency on the entity
  // identity ensures we don't clobber typed input on filter
  // refetches that return the same data).
  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const cancelHref = mode === "create"
    ? "/dashboard/accounts"
    : `/dashboard/accounts/${accountId ?? ""}`;
  const backLabel = mode === "create"
    ? "Back to Accounts"
    : "Back to Account";
  const heading = mode === "create" ? "New Account" : "Edit Account";
  const subtitle = mode === "create"
    ? "Add a new employer Account to track renewals, contacts, and Commitments."
    : "Update Account details used across renewals, Commitments, and reporting.";

  function handleChange<K extends keyof AccountFormValues>(
    key: K, value: AccountFormValues[K],
  ): void {
    setValues((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }

  function handleSubmit(): void {
    const { errors: validationErrors, ok } = validateAccountForm(values);
    if (!ok) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    const payload = serializeFormValues(values);
    if (mode === "create") {
      createMutation.mutate(payload, {
        onSuccess: (data) => {
          navigate(`/dashboard/accounts/${data.account_id}`, {
            replace: true,
            state: { saved: "created" },
          });
        },
      });
    } else if (accountId) {
      updateMutation.mutate(
        { account_id: accountId, values: payload },
        {
          onSuccess: () => {
            navigate(`/dashboard/accounts/${accountId}`, {
              replace: true,
              state: { saved: "updated" },
            });
          },
        },
      );
    }
  }

  // Loading / error / not-found gates only apply in edit mode —
  // create mode never reads an existing Account.
  if (mode === "edit") {
    if (detailQuery.isLoading) {
      return (
        <div data-testid="account-form-page" className="flex flex-col gap-5">
          <DashboardLoadingState variant="card" />
        </div>
      );
    }
    if (detailQuery.isError) {
      if (detailQuery.error instanceof AccountNotFoundError) {
        return (
          <div
            data-testid="account-detail-not-found"
            className="flex flex-col gap-5"
          >
            <DashboardEmptyState
              title="Account not found"
              description="It may have been archived, or the URL is wrong."
              action={
                <Link
                  to="/dashboard/accounts"
                  className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90"
                >
                  Back to Accounts
                </Link>
              }
            />
          </div>
        );
      }
      return (
        <div data-testid="account-form-page" className="flex flex-col gap-5">
          <DashboardErrorState
            title="Couldn't load this Account"
            description="Refresh to retry, or check the network connection."
            retry={() => detailQuery.refetch()}
          />
        </div>
      );
    }
  }

  const mutation = mode === "create" ? createMutation : updateMutation;
  const mutationError = mutation.isError
    ? (mutation.error as Error)
    : null;

  return (
    <div data-testid="account-form-page" className="flex flex-col gap-5">
      <header className="flex flex-col gap-2">
        <Link
          to={cancelHref}
          data-testid="account-form-back"
          className="inline-flex w-fit items-center gap-1 text-xs font-medium text-text-secondary hover:text-text-primary"
        >
          <ArrowLeft size={12} aria-hidden="true" />
          {backLabel}
        </Link>
        <h1 className="text-lg font-semibold tracking-tight text-text-primary">
          {heading}
        </h1>
        <p className="max-w-xl text-sm text-text-secondary">{subtitle}</p>
      </header>
      <AccountForm
        mode={mode}
        values={values}
        errors={errors}
        onChange={handleChange}
        onSubmit={handleSubmit}
        isPending={mutation.isPending}
        mutationError={mutationError}
        cancelHref={cancelHref}
      />
    </div>
  );
}


function deriveInitialValues(
  mode: AccountFormMode,
  detail: AccountDetail | undefined,
): AccountFormValues {
  if (mode === "create" || !detail) return blankFormValues();
  return {
    legal_name: detail.legal_name ?? "",
    dba_name: detail.dba_name ?? "",
    ein: detail.ein ?? "",
    industry: detail.industry ?? "",
    employee_count_total:
      detail.employee_count_total != null
        ? String(detail.employee_count_total)
        : "",
    employee_count_eligible:
      detail.employee_count_eligible != null
        ? String(detail.employee_count_eligible)
        : "",
    bor_signed_at: toDateInputValue(
      detail.bor_signed_at ?? detail.bor_date ?? null,
    ),
    primary_address_line1: detail.primary_address_line1 ?? "",
    primary_address_line2: detail.primary_address_line2 ?? "",
    primary_address_city: detail.primary_address_city ?? "",
    primary_address_state: detail.primary_address_state ?? "",
    primary_address_zip: detail.primary_address_zip ?? "",
    notes: detail.notes ?? "",
    primary_location_id: detail.primary_location_id ?? "",
    naics_sector: detail.naics_sector ?? "",
    naics_code: detail.naics_code ?? "",
    naics_title: detail.naics_title ?? "",
    risk_class: detail.risk_class ?? "",
    account_tag_ids: (detail.account_tags ?? []).map((t) => t.tag_id),
  };
}


function toDateInputValue(value: string | null): string {
  if (!value) return "";
  // Backend returns full ISO datetime (``YYYY-MM-DDTHH:mm:ss+00:00``);
  // ``<input type="date">`` only accepts ``YYYY-MM-DD``.
  return value.length >= 10 ? value.slice(0, 10) : value;
}
